import { ChangeDetectionStrategy, Component, HostListener, inject, OnInit, signal } from '@angular/core'
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router'
import { HeaderComponent } from '@app/layout/header/header.component'
import { ToolbarComponent } from '@app/layout/toolbar/components/toolbar.component'
import { SidebarComponent } from '@app/layout/sidebar/sidebar.component'
import { FooterComponent } from '@app/layout/footer/footer.component'
import { DrawerModule } from 'primeng/drawer'
import { AlbertModalComponent } from '@shared/components/albert-modal/albert-modal.component'
import { GlobalErrorService } from '@services/global-error.service'
import { AlbertModalService } from '@services/albert-modal.service'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { filter, map } from 'rxjs'
import { UnsavedChangesGuard } from '@app/shared/services/unsaved-changes.guard'
import { CurrencyService } from '@app/shared/services/currency.service'
import { ScrollTopModule } from 'primeng/scrolltop'
import { PrimeNG } from 'primeng/config'
import { CountryService } from '@services/country.service'

@Component({
  selector: 'albert-app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    ToolbarComponent,
    SidebarComponent,
    FooterComponent,
    DrawerModule,
    AlbertModalComponent,
    ScrollTopModule
  ],
  template: `
    @if (withoutLayout()) {
      <router-outlet/>
    } @else {
      <div class="min-h-screen bg-gray-50 min-w-90">
        <div class="w-full bg-white shadow-sm xl:mx-auto xl:max-w-480">
          <header class="sticky top-0 z-15">
            <albert-header
              [(sidebarDrawerVisible)]="sidebarOpen"
              [(toolbarDrawerVisible)]="toolbarDrawerOpen"
            />
            <albert-toolbar [(toolbarDrawerVisible)]="toolbarDrawerOpen"/>
          </header>
          <div class="flex flex-1 min-h-0">
            <aside class="w-76 border-r border-gray-300 hidden md:block sticky top-29 h-[calc(100dvh-7.25rem)]
                          min-[1201px]:top-39 min-[1201px]:h-[calc(100dvh-9.75rem)]"
            >
              <albert-sidebar/>
            </aside>

            <p-drawer styleClass="sidebar-mobile-drawer"
                      data-test="sidebar-drawer"
                      [(visible)]="sidebarOpen"
            >
              <ng-template #header>
                <div class="flex flex-col items-start">
                  <div class="navigation-menu-title leading-tight">List View</div>
                </div>
              </ng-template>
              <ng-template #content>
                <albert-sidebar [(sidebarDrawerVisible)]="sidebarOpen"/>
              </ng-template>
            </p-drawer>
            <div class="flex flex-1 min-w-0 flex-col">
              <main class="p-3 md:p-4 flex-1">
                <router-outlet/>
              </main>
              <footer>
                <albert-footer/>
              </footer>
            </div>
          </div>
        </div>
        <albert-modal slot="content"/>
        <albert-modal slot="prompt"/>
        <albert-modal/>
        <p-scroll-top/>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit {
  protected readonly sidebarOpen = signal(false)
  protected readonly toolbarDrawerOpen = signal(false)

  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly albertModalService = inject(AlbertModalService)
  private readonly unsavedChangesGuard = inject(UnsavedChangesGuard)
  private readonly currencyService = inject(CurrencyService)
  private readonly countryService = inject(CountryService)
  private readonly primeConfig = inject(PrimeNG)
  private readonly router = inject(Router)

  /** Routes flagged with `data.withoutLayout` (e.g. the document preview) render without header, toolbar and sidebar. */
  protected readonly withoutLayout = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.deepestRoute(this.router.routerState.snapshot.root).data['withoutLayout'] === true)
    ),
    { initialValue: false }
  )

  constructor() {
    this.globalErrorService.error$.pipe(takeUntilDestroyed()).subscribe((error) => {
      this.albertModalService.show({
        title: 'Technical Error',
        subsystemName: `${error.subsystemName}`,
        message: `${error.description}`,
        type: 'error',
        actions: [
          {
            label: 'Close',
            position: 'right',
            severity: 'secondary',
            callback: () => this.albertModalService.hide(),
          },
        ],
        closable: true,
      })
    })

    // Load currencies on application startup
    this.currencyService.loadCurrencies().subscribe()
    this.countryService.loadCountries().subscribe()
  }

  public ngOnInit(): void {
    this.primeConfig.setTranslation({
      dateFormat: 'dd.mm.yy',
      firstDayOfWeek: 1,
    })
  }

  private deepestRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    return route.firstChild ? this.deepestRoute(route.firstChild) : route
  }

  @HostListener('window:beforeunload', ['$event'])
  public onBeforeUnload(event: BeforeUnloadEvent): void {
    // If the guard has determined there are unsaved changes
    if (this.unsavedChangesGuard.hasUnsavedChanges()) {
      // Best practice according to https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
      // Using event.returnValue is deprecated, thus ESLint would show an error
      event.preventDefault()
    }
  }
}
